import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common'; // Need CommonModule for *ngIf if not entirely moved to new control flow
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastComponent } from './toast/toast.component';
import { VersionService } from './services/version.service';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, MatTabsModule, MatDividerModule, MatIconModule, MatButtonModule, MatToolbarModule, ToastComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'fishing-app';
  isNavVisible: boolean = true;
  @ViewChild('mainContent') mainContentRef?: ElementRef<HTMLElement>;

  private readonly pullDistanceThreshold = 80;
  private readonly maxHorizontalDrift = 50;
  private readonly maxPullDurationMs = 2500;
  private touchStartY: number | null = null;
  private touchStartX: number | null = null;
  private touchStartTime = 0;
  private isPullEligible = false;
  private pullDistance = 0;
  private routerSub!: Subscription;

  constructor(private router: Router, private versionService: VersionService) {}

  ngOnInit() {
    // Automatically hide navbar when navigating to the fishing route
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      if (event.urlAfterRedirects.includes('/fishing')) {
        this.isNavVisible = false;
      } else {
        this.isNavVisible = true;
      }
      this.versionService.check();
    });
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  toggleNav() {
    this.isNavVisible = !this.isNavVisible;
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      this.resetPullState();
      return;
    }

    const mainContent = this.mainContentRef?.nativeElement;
    if (!mainContent) {
      this.resetPullState();
      return;
    }

    this.touchStartY = event.touches[0].clientY;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartTime = Date.now();
    this.pullDistance = 0;

    const insideMapCanvas = (event.target as HTMLElement | null)?.closest('.mapboxgl-canvas-container') !== null;
    this.isPullEligible = mainContent.scrollTop <= 0 && !insideMapCanvas;
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isPullEligible || this.touchStartY === null || this.touchStartX === null || event.touches.length !== 1) {
      return;
    }

    const currentY = event.touches[0].clientY;
    const currentX = event.touches[0].clientX;
    const deltaY = currentY - this.touchStartY;
    const deltaX = Math.abs(currentX - this.touchStartX);

    if (deltaY > 0 && deltaX <= this.maxHorizontalDrift) {
      this.pullDistance = deltaY;
    } else {
      this.isPullEligible = false;
    }
  }

  onTouchEnd(): void {
    const pullDuration = Date.now() - this.touchStartTime;
    const shouldRefresh = this.isPullEligible
      && this.pullDistance >= this.pullDistanceThreshold
      && pullDuration <= this.maxPullDurationMs;

    this.resetPullState();

    if (shouldRefresh) {
      this.refreshApp();
    }
  }

  private resetPullState(): void {
    this.touchStartY = null;
    this.touchStartX = null;
    this.pullDistance = 0;
    this.isPullEligible = false;
    this.touchStartTime = 0;
  }

  private refreshApp(): void {
    const isPwa = window.matchMedia('(display-mode: standalone)').matches
      || ((window.navigator as Navigator & { standalone?: boolean }).standalone === true);

    if (isPwa) {
      const url = new URL(window.location.href);
      url.searchParams.set('_hard_refresh', Date.now().toString());
      window.location.replace(url.toString());
      return;
    }

    window.location.reload();
  }
}
